import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { colors, spacing, radius, easing, typography } from "../../design-system/tokens";

// Logo animation
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-2px) rotate(0.3deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

// Logo components
export const LogoContainer = styled.div`
  position: relative;
  width: 28px;
  height: 28px;
  cursor: pointer;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

export const LogoShapeContainer = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
  transform: perspective(1000px) rotateX(3deg) rotateY(-3deg);
  transition: transform 0.6s ${easing.smooth};
  animation: ${float} 8s ease-in-out infinite;

  &:hover {
    transform: perspective(1000px) rotateX(5deg) rotateY(-5deg);
  }
`;

export const LogoShape = styled.div<{ $variant: 'primary' | 'secondary' }>`
  width: 16px;
  height: 16px;
  position: absolute;
  border-radius: ${radius.sm};
  transition: all 0.6s ${easing.smooth};
  mix-blend-mode: multiply;
  
  ${props => props.$variant === 'primary' ? `
    background: rgba(107, 70, 193, 0.9);
    transform: translate(6px, 6px);
    z-index: 2;
    box-shadow: 0 3px 8px rgba(107, 70, 193, 0.2);
    
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to bottom right,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      border-radius: inherit;
    }
  ` : `
    background: rgba(0, 181, 216, 0.9);
    transform: translate(0px, 0px);
    z-index: 1;
    box-shadow: 0 3px 8px rgba(0, 181, 216, 0.2);
    
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to bottom right,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      border-radius: inherit;
    }
  `}

  &:hover {
    transform: ${props => props.$variant === 'primary' 
      ? 'translate(7px, 7px) scale(1.03)'
      : 'translate(-1px, -1px) scale(1.03)'
    };
  }
`;

// User info section
export const UserSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 28px;
  padding: 0 16px;
  margin-bottom: 10px;
`;

export const LogoUserWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

// ======================
// SIDEBAR COMPONENTS
// ======================

// Fixed collapsed sidebar
export const SidebarContainer = styled.div`
  width: 56px;
  height: 100%;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 200;
  overflow: hidden;
  border-radius: 0 16px 16px 0;
  flex-shrink: 0;
`;

// Library icon button style (using burger design) - replaces the nav icon entirely
export const LibraryIconButton = styled.button<{ $isActive?: boolean }>`
  width: 28px;
  height: 28px;
  background: ${props => props.$isActive ? '#6b7280' : 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(8px);
  border: 1px solid ${props => props.$isActive ? '#6b7280' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${props => props.$isActive ? '#fafbfc' : '#6b7280'};

  &:hover {
    background: ${props => props.$isActive ? '#374151' : 'rgba(255, 255, 255, 0.15)'};
    border-color: ${props => props.$isActive ? '#374151' : 'rgba(255, 255, 255, 0.3)'};
    color: ${props => props.$isActive ? '#fafbfc' : '#374151'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
    transition: all 0.2s ease;
  }
`;

export const SidebarContent = styled.div`
  padding: 8px 0 16px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  overflow: hidden;
  height: 100%;
`;

// Universal sidebar item wrapper with consistent hover and alignment
export const SidebarItemWrapper = styled.div<{ $isClickable?: boolean }>`
  position: relative;
  width: 100%;
  height: 28px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 6px;
  transition: all 0.15s ease;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};

  &:hover {
    background: ${props => props.$isClickable ? 'rgba(255, 255, 255, 0.4)' : 'transparent'};
    backdrop-filter: ${props => props.$isClickable ? 'blur(4px)' : 'none'};
  }
`;

// Main navigation container
export const MainNavigation = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

// Fixed bottom section
export const BottomSection = styled.div`
  width: 100%;
  margin-top: auto;
  padding-top: 12px;
`;

export const NavLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  width: 100%;
`;

export const NavLinkContent = styled.div<{ $isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) =>
    props.$isActive ? colors.brand.primary : colors.text.tertiary};
  transition: all 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
    flex-shrink: 0;
    opacity: ${props => props.$isActive ? 1 : 0.8};
    transition: all 0.15s ease;
  }

  span {
    display: none;
  }
`;

export const NavLink = styled(Link)<{ $isActive?: boolean }>`
  text-decoration: none;
  color: inherit;
  width: 100%;
  
  &:hover ${NavLinkContent} {
    color: ${(props) =>
      props.$isActive
        ? colors.brand.primary
        : colors.text.secondary};
        
    svg {
      opacity: 1;
    }
  }
`;

// ======================
// LIBRARY EXTENSION COMPONENTS
// ======================

export const ExtensionContainer = styled.div<{ $isOpen: boolean; $mainSidebarWidth: number }>`
  width: ${props => props.$isOpen ? '280px' : '0px'};
  height: 100vh;
  background: #fafbfc;
  border: none;
  border-radius: 0 12px 12px 0;
  border-right: ${props => props.$isOpen ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'};
  box-shadow: ${props => props.$isOpen ? '2px 0 8px rgba(0, 0, 0, 0.04)' : 'none'};
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
`;

export const ExtensionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: none;
  background: transparent;
  min-height: 44px;
`;

export const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  text-transform: none;
  letter-spacing: 0;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #374151;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  background: transparent;
  color: #6b7280;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  font-size: 9px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.02);
    color: #374151;
    border-color: rgba(0, 0, 0, 0.12);
  }
  
  svg {
    width: 10px;
    height: 10px;
  }
`;

export const SelectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  background: transparent;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  font-size: 11px;
  color: #6b7280;
`;

export const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;

export const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
`;

export const EmptyStateIcon = styled.div`
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  
  svg {
    width: 18px;
    height: 18px;
    color: #9ca3af;
  }
`;

export const EmptyStateTitle = styled.h4`
  margin: 0 0 6px 0;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
`;

export const ItemsList = styled.div`
  padding: 8px;
`;

export const CreatorItem = styled.div<{ $isSelected: boolean; $isDragging?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 1px;
  border-radius: 6px;
  transition: all 0.15s ease;
  background: ${props => {
    if (props.$isDragging) return '#e5e7eb';
    return props.$isSelected ? '#f3f4f6' : 'transparent';
  }};
  border: 1px solid transparent;
  opacity: ${props => props.$isDragging ? 0.5 : 1};
  color: ${props => props.$isSelected ? '#374151' : '#6b7280'};
  font-size: 11px;
  
  &:hover {
    background: ${props => {
      if (props.$isDragging) return '#e5e7eb';
      return props.$isSelected ? '#e5e7eb' : '#f9fafb';
    }};
    color: #374151;
  }
`;

export const CreatorCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 12px;
  height: 12px;
  cursor: pointer;
  flex-shrink: 0;
  margin: 0;
`;

export const CreatorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  font-size: 10px;
  font-weight: 500;
  color: #9ca3af;
`;

export const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

export const AvatarFallback = styled.div`
  color: #9ca3af;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const CreatorInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const CreatorName = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CreatorStats = styled.div`
  font-size: 10px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PlatformBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0.8;
`;

export const CreatorActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
  
  svg {
    width: 10px;
    height: 10px;
    color: #6b7280;
  }
`;

// Additional styled components for inline styles cleanup
export const SelectedCountSpan = styled.span`
  color: #374151;
  font-weight: 500;
`;

export const EmptyStateDescription = styled.div`
  font-size: 10px;
  opacity: 0.7;
  color: #6b7280;
  margin-bottom: ${spacing[4]};
`;

export const CreateCreatorButton = styled.button`
  background: ${colors.brand.primary};
  color: ${colors.palette.white};
  border: none;
  border-radius: 6px;
  padding: ${spacing[1]} ${spacing[3]};
  font-size: ${typography.fontSizes.sm};
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  
  &:hover {
    background: ${colors.brand.primaryHover};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

// Alert component (Stripe-style - very subtle)
export const InfoAlert = styled.div`
  background: rgba(99, 102, 241, 0.04);
  border: 1px solid rgba(99, 102, 241, 0.12);
  border-radius: 8px;
  padding: ${spacing[2]} ${spacing[3]};
  margin: ${spacing[2]} ${spacing[4]} ${spacing[3]};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  animation: slideDown 0.2s ease;
  position: relative;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const InfoAlertIcon = styled.div`
  flex-shrink: 0;
  color: #6366f1;
  display: flex;
  align-items: center;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const InfoAlertContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const InfoAlertTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #4f46e5;
  margin-bottom: 2px;
  letter-spacing: -0.01em;
`;

export const InfoAlertMessage = styled.div`
  font-size: 10px;
  color: #6b7280;
  line-height: 1.5;
  letter-spacing: -0.01em;
`;

// Skeleton loading components
export const SkeletonCreatorItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 1px;
  border-radius: 6px;
  background: transparent;
`;

export const SkeletonCheckbox = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  flex-shrink: 0;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

export const SkeletonAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  flex-shrink: 0;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

export const SkeletonContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SkeletonLine = styled.div<{ $width?: string; $height?: string }>`
  height: ${props => props.$height || '10px'};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 3px;
  width: ${props => props.$width || '100%'};

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

// Managers dropdown
export const ManagersDropdown = styled.div`
  position: absolute;
  left: 64px;
  top: 0;
  width: 280px;
  background: white;
  border: 1px solid ${colors.border.secondary};
  border-radius: ${radius.md};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  overflow: hidden;
  pointer-events: all;
`;

export const DropdownTitle = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.secondary};
  background: ${colors.palette.gray[25]};
  border-bottom: 1px solid ${colors.border.secondary};
`;

export const ManagerOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  cursor: pointer;
  transition: all 0.15s ease;
  border-bottom: 1px solid ${colors.border.secondary};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.background.hover};
  }
`;

export const ManagerEmoji = styled.div`
  font-size: 24px;
  line-height: 1;
  flex-shrink: 0;
`;

export const ManagerDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ManagerName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

export const ManagerRole = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`; 